/*
 * ls-elements
 * Copyright (C) 2024 Libriciel SCOP
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {
  Component,
  ContentChild, EventEmitter,
  Input, Output,
  TemplateRef, ViewEncapsulation
} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {SpinnerComponent} from "../spinner/spinner.component";
import { TableHeader } from 'src/app/model/table-header';
import { TableAction } from 'src/app/model/table-action';
import { MinPipe } from '../MinPipe';


@Component({
  selector: 'ls-dynamic-table',
  standalone: true,
  imports: [
    CommonModule,
    FaIconComponent,
    NgbTooltip,
    SpinnerComponent,
    MinPipe,
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './dynamic-table.component.html',
  styleUrl: './dynamic-table.component.scss'
})
/**
 * Dynamic table, should contain <ng-template let-item> where item is single object data
 * Inputs "headers" is mandatory.
 *
 * To use with dynamic data, [fulldata] should be false and [totalPages] should be defined
 */
export class DynamicTableComponent {
  /**
   * Tracker for the loop
   */
  @Input() track?:string;
  /**
   * Should we scroll up on page change
   */
  @Input() autoscroll: boolean = true;
  /**
   * Display pagination on top
   */
  @Input() paginationTop: boolean = true;
  /**
   * Display pagination on bottom
   */
  @Input() paginationBottom: boolean = true;
  /**
   * Is full data sent, or is it dynamic
   */
  @Input() fulldata: boolean = true;
  /**
   * If data is dynamic, we need to know the total number of elements
   */
  @Input() total: number = 1;
  /**
   * Force spinner display when dynamic data is loading
   */
  @Input() loadingData: boolean = false;
  /**
   * Table Headers definition
   * @see TableHeader
   */
  @Input() set headers(headers: TableHeader<any>[]) {
    this._headers = headers ?? [];
    // headers peut arriver après data : ré-applique le tri courant
    if(this.fulldata && this._data && this._currentSort) {
      this.applySort();
      this.updateDataPage();
    }
  }

  get headers(): TableHeader<any>[] {
    return this._headers;
  }

  _headers: TableHeader<any>[] = [];
  /**
   * Table Actions definition
   * @see TableAction
   */
  @Input() actions:  TableAction<any>[] = [];

  /**
   * Enable pagination
   * @param paginate Enable pagination
   */
  @Input() set paginate(paginate: boolean) {
    this._paginate = paginate;
    this.updateDataPage();
  };

  /**
   * Set page size if pagination is enabled
   * @param size The size of the page
   */
  @Input() set pagesize(size: number) {
    this._pagesize = size;
    this.updateDataPage();
  };

  /**
   * Data to display on table
   * @param items list of items
   */
  @Input() set data(items: any[]) {
    this._data = items;
    if(this._data) {
      if(this.fulldata) {
        this._page = 1;
        // Applique le tri courant SANS basculer l'ordre :
        // ce setter est réémis à chaque changement de données.
        this.applySort();
      }
      this.updateDataPage();
    }
  };

  /**
   * Define current page
   * @param page
   */
  @Input() set page(page: number) {
    this._page = page;
    this.pageChange.emit(this._page);
    this.updateDataPage();
  }

  /**
   * Emit when page change
   */
  @Output()
  pageChange: EventEmitter<number> = new EventEmitter();

  /**
   * Function resolver for custom css class on table's rows
   */
  @Input() rowCustomClass?: (item: any) => string;
  /**
   * Function resolver for tooltip on table's rows
   */
  @Input() rowTooltip?: (item: any) => string;

  _data!: any[];
  _paginatedData?: any[];
  _page = 1;
  _pagesize = 10;
  _paginate = false;
  _currentSort!: string;
  _sortAsc = false;

  @Input() bodyTemplate!: TemplateRef<any>;
  @ContentChild(TemplateRef) templateRef!: TemplateRef<any>;

  /**
   * Colonne de tri appliquée à l'affichage.
   * ATTENTION : un two-way binding [(currentSort)] réémet la valeur après un clic
   * dans la table ; le setter ne doit donc PAS basculer l'ordre. Il se contente
   * de ré-appliquer le tri courant (idempotent), ce qui couvre aussi le cas où
   * data est settée avant currentSort (ordre des attributs du template).
   */
  @Input()
  set currentSort(sort: string) {
    this._currentSort = sort;
    this.applySort();
  }

  @Output()
  currentSortChange: EventEmitter<string> = new EventEmitter();

  /**
   * Ordre de tri appliqué à l'affichage (true = croissant).
   * ATTENTION : un two-way binding [(sortAsc)] réémet la valeur après un clic
   * dans la table ; le setter ne doit donc PAS basculer l'ordre. Il ré-applique
   * le tri courant (idempotent), ce qui couvre aussi le cas où data est settée
   * avant sortAsc (ordre des attributs du template).
   * Coerce les attributs statiques non crochetés (sortAsc="false" → chaîne).
   */
  @Input()
  set sortAsc(asc: boolean | string) {
    if (asc === 'true') {
      this._sortAsc = true;
    } else if (asc === 'false') {
      this._sortAsc = false;
    } else {
      this._sortAsc = !!asc;
    }
    this.applySort();
  }

  @Output()
  sortAscChange: EventEmitter<boolean> = new EventEmitter();

  /**
   * Gestion du clic sur un en-tête : bascule l'ordre si on re-clique la même colonne,
   * sinon nouvelle colonne en ordre croissant, puis émet les nouvelles valeurs.
   */
  sort(h: TableHeader<any>): void {
    if(h.sortable) {
      if(h.property) {
        if(this._currentSort === h.property) {
          this._sortAsc = !this._sortAsc;
        } else {
          this._currentSort = h.property;
          this._sortAsc = true;
        }
        this.sortAscChange.emit(this._sortAsc);
        this.currentSortChange.emit(this._currentSort);
      }
      this.applySort();
    }
    this.updateDataPage();
  }

  /**
   * Applique le tri courant (_currentSort / _sortAsc) aux données,
   * sans modifier l'ordre (contrairement à sort()).
   */
  applySort(): void {
    if(!this.fulldata || !this._data || !this._currentSort) {
      return;
    }
    const header = this.headers.find(h => h.property === this._currentSort && h.sortable);
    if(!header) {
      return;
    }
    if(header.customSort) {
      this._data.sort(header.customSort);
      if(!this._sortAsc) {
        this._data.reverse();
      }
    } else {
      this._data.sort(this.dynamicSort(this._currentSort));
    }
    this.updateDataPage();
  }

  /**
   * This is only for string and number, does not work for objects
   * @param property The property key
   */
  dynamicSort(property: string) {
    let sortOrder = this._sortAsc ? 1 : -1;
    return function (a:any, b:any) {
      let astr;
      let bstr;
      if(a[property] instanceof Date) {
        astr = a[property].getTime();
        bstr = b[property].getTime();
      } else {
        if(a[property].toLowerCase && b[property].toLowerCase) {
          astr = ('' + a[property]).toLowerCase();
          bstr = ('' + b[property]).toLowerCase();
        } else {
          astr = ('' + a[property]).toLowerCase();
          bstr = ('' + b[property]).toLowerCase();
        }
      }
      const result = (astr < bstr) ? -1 : (astr > bstr) ? 1 : 0;
      return result * sortOrder;
    }
  }

  updateDataPage() {
    if(this._data) {
      if(this._paginate) {
        this._paginatedData = this._data.slice((this._page - 1) * this._pagesize, this._page * this._pagesize);
      } else {
        this._paginatedData = this._data;
      }
      if(this.autoscroll) {
        window.scrollTo(0, 0);
      }
    }
  }

  nextPage(): void {
    if (this._page < (this.fulldata ? this._data.length : this.total) / this._pagesize) {
      this.page = this._page + 1;
    }
  }

  previousPage(): void {
    if (this._page > 1) {
      this.page = this._page - 1;
    }
  }
}
